import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState} from '@/app/store';
import {HttpMethod} from '@/constants/httpMethod';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setBearerTokenIfExists, setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBaseCards} from './constants';
import {
  IFetchCard,
  ScheduleCardRequest,
  ScheduleCardResponse,
} from './entities';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;
      setBearerTokenIfExists(headers, token);
      await setWafHeaders(baseUrl, headers);
      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBaseCards.baseUrl;

  const result = await reAuth(args, api, extraOptions, baseUrl, baseQuery);
  return result;
};

export const cardsApi = createApi({
  reducerPath: EReducersPath.CARDS_API,
  refetchOnReconnect: true,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    getCards: build.query<IFetchCard[], void>({
      query: () => apiBaseCards.endpoints.getCards,
    }),
    schedulePhysicalCard: build.mutation<
      ScheduleCardResponse,
      ScheduleCardRequest
    >({
      query: body => ({
        url: apiBaseCards.endpoints.schedulePhysicalCard,
        method: HttpMethod.POST,
        body,
      }),
    }),
  }),
});

export const {useLazyGetCardsQuery, useSchedulePhysicalCardMutation} = cardsApi;
